import React from 'react';
import { Box, Typography, Card, CardMedia, useMediaQuery, useTheme, IconButton, Link } from '@mui/material';
import { Verified as VerifiedIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getCachedImage } from '../utils/imageCache';
import { ArrowForwardIos, ArrowBackIos } from '@mui/icons-material';
import { useState, useEffect } from 'react';
import FeaturedProductsCard from './FeaturedProductCard';
import LdPlaceHolder from '../assets/ldplaceholder.png';
import { UNIT_LABELS_SINGULAR, UNIT_LABELS_PLURAL } from '../utils/units';

const PopularProductsShowcase = ({ products }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  
  if (!products?.length) {
    return (
      <Box sx={{ 
        width: 'auto', 
        height: '100%',
        py: 5,
        backgroundColor: '#fff',
      }}>
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          justifyContent: 'center'
        }}>
          {Array.from({ length: isMobile ? 2 : 4 }).map((_, index) => (
            <Card
              key={index}
              sx={{
                width: isMobile ? '25vw' : '24%',
                height: isMobile ? '220px' : '320px',
                borderRadius: '8px',
                bgcolor: '#f5f5f5',
                animation: 'pulse 1.5s infinite ease-in-out',
                '@keyframes pulse': {
                  '0%': { opacity: 1 },
                  '50%': { opacity: 0.7 },
                  '100%': { opacity: 1 },
                },
              }}
            />
          ))}
        </Box>
      </Box>
    );
  }

  // Calculate how many complete rows of 4 products we can show
  const productsToShow = products.slice(0, Math.floor(products.length / (isMobile ? 2 : 4)) * (isMobile ? 2 : 4));

  return (
    <Box sx={{ 
      width: 'auto', 
      height: 'auto',
      pb: 4,
      py: 1,
      mx: isMobile ? '0' : '11.4vw',
      px:'0 !important',
      backgroundColor: 'transparent',
    }}>
      <Box sx={{ 
        display: 'flex',
        width: 'auto',
        flexWrap: 'wrap',
        gap: '8px',
        px:0,
        justifyContent: 'center'
      }}>
        {productsToShow.map((product) => (
          <ProductCard 
            key={product._id} 
            product={product} 
            isMobile={isMobile}
            onClick={() => navigate(`/product/${product._id}`)}
          />
        ))}
      </Box>
    </Box>
  );
};

const ProductCard = ({ product, isMobile, onClick }) => {
  const formattedPrice = new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format((product.priceBreaks?.length > 0 ? product.priceBreaks[product.priceBreaks.length - 1].price : product.basePrice));

  return (
    <Card
      onClick={onClick}
      component={Link}
      to={`/product/${product._id}`}
      href={`/product/${product._id}`}
      sx={{
        width: isMobile ? '42.5vw' : '24.4%',
        height: isMobile ? '46vh' : '420px',
        backgroundColor: 'white',
        borderRadius: '1px 1px 8px 8px',
        overflow: 'hidden',
        textDecoration: 'none',
        position: 'relative',
        cursor: 'pointer',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
        },
        boxShadow: '0px 0px 2px 0px rgba(0, 0, 0, 0.1)',
      }}
    >
      <meta itemProp="name" content={product.name} />
      <meta itemProp="description" content={`${product.name} - ${product.distributorName}`} />
      <meta itemProp="price" content={product.priceBreaks?.length > 0 ? product.priceBreaks[product.priceBreaks.length - 1].price : product.basePrice} />
      
      <CardMedia
        component="img"
        image={product.imageUrls?.[0] ? getCachedImage(product.imageUrls?.[0]?.replace('upload/', 'upload/q_auto/')) : LdPlaceHolder}
        alt={product.name}
        itemProp="image"
        sx={{
          height: '65%',
          objectFit: 'contain',
          p:2
        }}
      />
      
      <Box sx={{ 
        height: '35%',
        background: '#fffbf0',
        borderTop: '1px solid rgba(0,0,0,0.1)',
        color: 'black',
        p: isMobile ? 1 : 1.5,
        pt: isMobile ? 1 : 1.5,
      }}>
        <Typography 
                variant={isMobile ? "body2" : "body1"} 
          sx={{ 
            fontWeight: 'bold',
            mb: 0.5,
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: isMobile ? '1.2em' : '1.3em',
            maxHeight: isMobile ? '2.4em' : '2.6em',
            fontSize: {xs:'0.85rem', md:'1.2rem'},
          }}
        >
          {product.name}
        </Typography>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            
        <Typography sx={{fontSize: {xs:'0.8rem', md:'1rem'}}}>
            {product.distributorName}
        </Typography>
        <VerifiedIcon sx={{fontSize: {xs:'0.9rem', md:'1rem'}}}/>
        </Box>
        <Box sx={{display: 'flex', flexDirection: {xs:'column', md:'row'}, alignItems: { md:'center'}, gap: {xs:'0', md:0.5}}}>
        <Typography 
          variant={isMobile ? "caption" : "body2"}
          sx={{ 
            opacity: 0.9,
            fontWeight: 600,
            fontSize: {xs:'1.1rem', md:'1.2rem'},
            mt: 'auto',
          }}
        >
          {formattedPrice}
        </Typography>
        <Typography variant="h6" sx={{fontWeight: '550', fontSize: {xs:'12px', md:'14px'}, color: 'rgb(255, 118, 27)', flexShrink: 1, lineHeight: 1.1}}>
          por {UNIT_LABELS_SINGULAR[product.unit]}  
        </Typography>
        </Box>
        <Typography variant="caption" color="rgb(94, 94, 94)" sx={{fontWeight: '500', fontSize: '12px'}}>
          {product.priceBreaks?.length > 0 
                          ? `Desde ${product.priceBreaks[product.priceBreaks.length - 1].minQuantity} ${UNIT_LABELS_PLURAL[product.unit]}`
                          : `Precio unitario`
                        }
        </Typography>
      </Box>
      {product.distributor?.profileImage &&
        <img src={getCachedImage(product.distributor?.profileImage)} alt={product.distributorName} 
        style={{
            width: isMobile? '40px' : '50px',
            height: isMobile? '40px' : '50px',
            borderRadius:'50%',
            position:'absolute',
            bottom:5,
            right:5,
            border:'1px solid rgba(0,0,0,0.1)',
            backgroundSize:'cover',
            backgroundPosition:'center',
            backgroundRepeat:'no-repeat'}} />}
    </Card>
  );
};

export default PopularProductsShowcase; 